import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { getFieldValuesKey } from "./useGetFieldValues";
import { useSnackbar } from "src/utils/useSnackbar";
import { AxiosError } from "axios";

export const updateFieldValueKey = "updateFieldValue" as const;

export type FieldValueUpdate = {
    actionPlanAction: string;
    actionFieldId: number;
    value?: string;
    position?: number;
    dataListOptionCodes?: string[] | null;
};

export type FieldValueResponse = {
    id: number;
    actionFieldId: number;
    value: string;
    position: number;
    dataListOptionCodes?: string[] | null;
};

const FieldValueResponseSchema = z.object({
    id: z.number(),
    actionFieldId: z.number(),
    value: z.string(),
    position: z.number(),
    dataListOptionCodes: z.union([z.string().array(), z.null(), z.undefined()]).optional(),
});

type Options = {
    actionPlanId: string;
    actionPlanActionId: string;
    fieldValueId: number | null;
    onSuccess?: (data: FieldValueResponse) => void;
    onError?: (error: any) => void;
};

export const useUpsertFieldValue = (options: Options) => {
    const { actionPlanId, actionPlanActionId, fieldValueId = null, onSuccess, onError } = options;
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();

    const mutation = useMutation({
        mutationKey: [updateFieldValueKey, actionPlanActionId, fieldValueId],
        mutationFn: async (fieldValueData: FieldValueUpdate): Promise<FieldValueResponse> => {
            const url =
                fieldValueId === null ?
                    `/api/action_plan_action_field_values`
                :   `/api/action_plan_actions/${actionPlanActionId}/field_values/${fieldValueId}`;

            const response =
                fieldValueId === null ?
                    await loggedInApiCall.post(url, fieldValueData)
                :   await loggedInApiCall.put(url, fieldValueData);

            // Validate response with Zod
            const parsedData = FieldValueResponseSchema.parse(response.data);
            return parsedData;
        },
        onSuccess: (data) => {
            // Invalidate related queries to refresh the UI
            queryClient.invalidateQueries([`getOneActionPlanActions`, actionPlanActionId, actionPlanId]);
            queryClient.refetchQueries([`getOneActionPlanActions`, actionPlanActionId, actionPlanId]);
            queryClient.invalidateQueries([`action_plan_action_property_contributions`]);
            queryClient.invalidateQueries([`action_plan_action_field_values`]);

            queryClient.invalidateQueries([getFieldValuesKey, actionPlanActionId]);

            onSuccess?.(data);
        },
        onError: (error: AxiosError<any>) => {
            if (error.status !== 422) {
                snackbar.forceClose();
                snackbar.setSnackbarProps({
                    type: "error",
                    message: error?.response?.data?.["hydra:description"] ?? error?.message,
                    forceAutoHideInMilisecond: 10000,
                });
            }

            onError?.(error);
        },
    });

    return mutation;
};
